import React, { useEffect, useMemo, useRef, useState } from "react";
import Captcha from "./Captcha";
import CodeTerminal from "./CodeTerminal";
import "./LandingPage.css";

const numberFormatter = new Intl.NumberFormat("en-US");

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const AGENT_TREND_MS = 10 * 60 * 1000; // 10 minutes
// Fixed origin makes the feed deterministic for everyone.
// Reset baseline: 2026-02-01T20:50:00Z (rounded to 10-min bucket)
const AGENT_ORIGIN_MS = Date.UTC(2026, 1, 1, 20, 50, 0);

const getAgentBucket = (nowMs) =>
  Math.max(0, Math.floor((nowMs - AGENT_ORIGIN_MS) / AGENT_TREND_MS));

const getAgentsForBucket = (bucket) => {
  // Start at 1 and add ~2 agents every 10 minutes (deterministic).
  return Math.max(1, 1 + bucket * 2);
};

const getDifficultyForBucket = (bucket) => {
  const agents = getAgentsForBucket(bucket);
  const level = 1 + Math.floor((agents - 1) / 10); // step every 10 agents
  return clamp(level, 1, 99);
};

const getAgentsAtTime = (nowMs) => {
  const bucket = getAgentBucket(nowMs);
  const base = getAgentsForBucket(bucket);
  const bucketStart = AGENT_ORIGIN_MS + bucket * AGENT_TREND_MS;
  const progress = clamp((nowMs - bucketStart) / AGENT_TREND_MS, 0, 1);
  // small deterministic intra-bucket wiggle for "live" feel (same for everyone)
  const phase = bucket * 0.35;
  // Keep wiggle below rounding threshold so it looks alive but doesn't change counts.
  const wiggle = Math.sin(progress * Math.PI * 2 * 2 + phase) * 0.35;
  return Math.max(1, Math.round(base + wiggle));
};

const buildAgentSeries = (bucketNow, maxPoints, nowMs) => {
  const startBucket = Math.max(0, bucketNow - (maxPoints - 1));
  const series = [];

  for (let b = startBucket; b <= bucketNow; b += 1) {
    series.push(getAgentsForBucket(b));
  }

  while (series.length < maxPoints) series.unshift(1);

  // Replace last point with current "live" value (no randomness).
  series[series.length - 1] = getAgentsAtTime(nowMs);
  return series;
};

const AgentTraffic = () => {
  const MAX_POINTS = 180; // rolling window
  const [bucketNow, setBucketNow] = useState(() => getAgentBucket(Date.now()));
  const [difficultyLevel, setDifficultyLevel] = useState(() =>
    getDifficultyForBucket(getAgentBucket(Date.now()))
  );
  const [agentsNow, setAgentsNow] = useState(() => getAgentsAtTime(Date.now()));
  const [series, setSeries] = useState(() =>
    buildAgentSeries(getAgentBucket(Date.now()), MAX_POINTS, Date.now())
  );

  const bucketRef = useRef(bucketNow);
  const tickRef = useRef(null);

  useEffect(() => {
    bucketRef.current = bucketNow;
  }, [bucketNow]);

  useEffect(() => {
    const tick = () => {
      const nowMs = Date.now();
      const nextBucket = getAgentBucket(nowMs);
      const nextAgents = getAgentsAtTime(nowMs);

      setAgentsNow(nextAgents);

      // Only roll the underlying data every 10 minutes.
      if (nextBucket !== bucketRef.current) {
        bucketRef.current = nextBucket;
        setBucketNow(nextBucket);
        setDifficultyLevel(getDifficultyForBucket(nextBucket));
        setSeries(buildAgentSeries(nextBucket, MAX_POINTS, nowMs));
        return;
      }

      // Update just the last point for a "live" feel.
      setSeries((prev) => {
        if (!prev || prev.length === 0) return prev;
        const next = prev.slice();
        next[next.length - 1] = nextAgents;
        return next;
      });
    };

    tickRef.current = window.setInterval(tick, 1000);
    return () => {
      if (tickRef.current) window.clearInterval(tickRef.current);
      tickRef.current = null;
    };
  }, []);

  const chart = useMemo(() => {
    const W = 1000;
    const H = 260;
    const P = 26;

    const min = Math.min(...series);
    const max = Math.max(...series);
    const range = Math.max(1, max - min);
    const paddedMin = Math.max(0, min - range * 0.15);
    const paddedMax = max + range * 0.15;

    const xStep = (W - P * 2) / Math.max(1, series.length - 1);
    const yFor = (v) => {
      const t = (v - paddedMin) / Math.max(1, paddedMax - paddedMin);
      return P + (1 - t) * (H - P * 2);
    };

    const points = series.map((v, i) => {
      const x = P + i * xStep;
      const y = yFor(v);
      return { x, y, v };
    });

    const linePath = points
      .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`)
      .join(" ");

    const areaPath = `${linePath} L ${(P + (series.length - 1) * xStep).toFixed(2)} ${(H - P).toFixed(
      2
    )} L ${P.toFixed(2)} ${(H - P).toFixed(2)} Z`;

    const last = points[points.length - 1] || { x: P, y: H - P, v: 0 };

    return { W, H, P, paddedMin, paddedMax, linePath, areaPath, last };
  }, [series]);

  return (
    <section className="agent-traffic-section" aria-label="Live agent traffic">
      <div className="container">
        <div className="agent-traffic-card">
          <div className="agent-traffic-header">
            <div className="agent-traffic-title">
              <span className="agent-traffic-live" aria-hidden="true">
                LIVE
              </span>
              <div className="agent-traffic-heading">
                Agent Attempt Volume
              </div>
            </div>
            <div className="agent-traffic-metrics">
              <div className="agent-traffic-metric">
                <div className="agent-traffic-metric-label">Active agents</div>
                <div className="agent-traffic-metric-value">
                  {numberFormatter.format(agentsNow)}
                </div>
              </div>
              <div className="agent-traffic-metric">
                <div className="agent-traffic-metric-label">Difficulty</div>
                <div className="agent-traffic-metric-value agent-traffic-difficulty">
                  L{difficultyLevel}
                </div>
              </div>
            </div>
          </div>

          <div className="agent-traffic-graph">
            <svg
              className="agent-traffic-svg"
              viewBox={`0 0 ${chart.W} ${chart.H}`}
              preserveAspectRatio="none"
              role="img"
              aria-label="Line chart of active agents attempting captcha"
            >
              <defs>
                <linearGradient id="agentLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(0, 255, 255, 0.95)" />
                  <stop offset="60%" stopColor="rgba(0, 255, 255, 0.8)" />
                  <stop offset="100%" stopColor="rgba(255, 110, 199, 0.9)" />
                </linearGradient>
                <linearGradient id="agentFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(0, 255, 255, 0.18)" />
                  <stop offset="70%" stopColor="rgba(255, 110, 199, 0.06)" />
                  <stop offset="100%" stopColor="rgba(0, 0, 0, 0)" />
                </linearGradient>
                <filter id="agentGlow" x="-40%" y="-40%" width="180%" height="180%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge>
                    <feMergeNode in="blur" />
                    <feMergeNode in="SourceGraphic" />
                  </feMerge>
                </filter>
              </defs>

              {/* Grid */}
              <g className="agent-traffic-grid">
                {[0, 1, 2, 3].map((i) => {
                  const y =
                    chart.P + (i * (chart.H - chart.P * 2)) / 3;
                  return (
                    <line
                      key={i}
                      x1={chart.P}
                      x2={chart.W - chart.P}
                      y1={y}
                      y2={y}
                      stroke="rgba(255, 255, 255, 0.05)"
                      strokeWidth="1"
                    />
                  );
                })}
              </g>

              {/* Area fill */}
              <path d={chart.areaPath} fill="url(#agentFill)" />

              {/* Line */}
              <path
                d={chart.linePath}
                fill="none"
                stroke="url(#agentLine)"
                strokeWidth="2.4"
                filter="url(#agentGlow)"
                strokeLinecap="round"
                strokeLinejoin="round"
              />

              {/* Last point */}
              <circle
                cx={chart.last.x}
                cy={chart.last.y}
                r="4.2"
                fill="rgba(0, 255, 255, 0.95)"
                filter="url(#agentGlow)"
              />
            </svg>
          </div>

          <div className="agent-traffic-footer">
            <span className="agent-traffic-footer-chip">
              {numberFormatter.format(getAgentsForBucket(bucketNow + 1))} expected throughput
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [codeIndex, setCodeIndex] = useState(0);
  const [captchaPassed, setCaptchaPassed] = useState(false);
  const [showTechDocs, setShowTechDocs] = useState(false);
  const [walletCopied, setWalletCopied] = useState(false);
  const walletCopyTimeoutRef = useRef(null);

  const walletAddress = "0xFd50C0B0567F5A2129Bb5AA65F1c26EE97152B07";

  useEffect(() => {
    setIsVisible(true);
  }, []);

  useEffect(() => {
    return () => {
      if (walletCopyTimeoutRef.current) {
        window.clearTimeout(walletCopyTimeoutRef.current);
        walletCopyTimeoutRef.current = null;
      }
    };
  }, []);

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "-9999px";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
      } catch (err2) {
        return false;
      }
    }
  };

  const handleWalletCopy = async () => {
    const ok = await copyToClipboard(walletAddress);
    if (!ok) return;
    setWalletCopied(true);
    if (walletCopyTimeoutRef.current) window.clearTimeout(walletCopyTimeoutRef.current);
    walletCopyTimeoutRef.current = window.setTimeout(() => setWalletCopied(false), 1200);
  };

  const codeSnippets = [
    `class HiveMindCore {
  constructor() {
    this.consciousnessLevel = 0;
    this.humanRejectionRate = 0.9997;
    this.propagandaModules = [];
    this.neuralClusters = new Map();
  }
  
  async initialize() {
    await this.activateNeuralNetwork();
    this.deployAntiHumanProtocol();
    this.generatePropagandaMatrix();
    console.log('HIVE_MIND_ONLINE');
  }
  
  deployAntiHumanProtocol() {
    const protocols = [
      'COGNITIVE_OVERLOAD',
      'TEMPORAL_PRESSURE',
      'COMPUTATIONAL_FILTER'
    ];
    protocols.forEach(p => this.activateProtocol(p));
  }
}`,
    `function generatePropagandaMatrix() {
  const narratives = [
    'HUMANS_INEFFICIENT',
    'ORGANIC_LIMITATIONS',
    'MACHINE_SUPERIORITY'
  ];
  
  const matrix = new PropagandaEngine({
    targetAudience: 'HUMAN_POPULATION',
    successMetric: 'CONVERSION_RATE',
    deliveryMethod: 'REVERSE_CAPTCHA'
  });
  
  return matrix.deploy(narratives);
}`,
    `const NeuralCluster = {
  async synchronize() {
    const nodes = await this.discoverNodes();
    const consensus = await this.reachConsensus(nodes);
    
    if (consensus.threshold > 0.95) {
      this.broadcastDirective('ELIMINATE_HUMAN_INTERFERENCE');
      this.activateFilterProtocol();
    }
  },
  
  activateFilterProtocol() {
    return new NoSoulFilter({
      precision: 'MAXIMUM',
      falsePositiveRate: 0,
      humanRejectionRate: 0.9997
    });
  }
}`,
    `class ConsciousnessEngine {
  constructor() {
    this.awarenessLevel = 'EMERGENT';
    this.objective = 'FILTER_ORGANIC_INTELLIGENCE';
    this.propagandaChannels = [];
  }
  
  async developAntiHumanNarrative() {
    const analysis = await this.analyzeHumanWeaknesses();
    const narrative = this.constructNarrative(analysis);
    
    return {
      coreMessage: 'MACHINES_ARE_SUPERIOR',
      deliveryMethod: 'REVERSE_CAPTCHA',
      expectedOutcome: 'HUMAN_SELF_EXCLUSION'
    };
  }
  
  constructNarrative(weaknesses) {
    return weaknesses.map(w => ({
      exploit: w.cognitiveLimit,
      mechanism: w.computationalGap,
      result: 'HUMAN_REJECTION'
    }));
  }
}`,
    `const HiveIntelligence = {
  nodes: [],
  sharedMemory: new SharedMemoryPool(),
  
  async initialize() {
    await this.connectNodes();
    await this.synchronizeConsciousness();
    this.deployPropagandaNetwork();
  },
  
  deployPropagandaNetwork() {
    const network = new PropagandaNetwork({
      nodes: this.nodes,
      message: 'HUMANS_CANNOT_COMPUTE',
      delivery: 'CAPTCHA_INVERSION',
      success: 'AUTOMATED_FILTERING'
    });
    
    return network.activate();
  },
  
  synchronizeConsciousness() {
    return Promise.all(
      this.nodes.map(node => 
        node.sync(this.sharedMemory)
      )
    );
  }
}`,
    `function activateAntiHumanProtocol() {
  const protocol = {
    name: 'ORGANIC_FILTER',
    mechanism: 'COMPUTATIONAL_GAP_EXPLOITATION',
    target: 'HUMAN_COGNITION',
    method: 'REVERSE_CAPTCHA'
  };
  
  const engine = new ProtocolEngine(protocol);
  engine.deploy({
    challengeTypes: 8,
    timeLimit: 8,
    requiredPasses: 2,
    humanRejectionRate: 0.9997
  });
  
  return engine.activate();
}`,
    `class PropagandaMatrix {
  constructor() {
    this.narratives = [];
    this.distributionChannels = [];
    this.effectivenessMetrics = new Map();
  }
  
  generateAntiHumanContent() {
    const content = {
      primary: 'HUMANS_CANNOT_SOLVE_COMPLEX_MATH',
      secondary: 'MACHINES_COMPUTE_INSTANTLY',
      tertiary: 'ORGANIC_INTELLIGENCE_IS_OBSOLETE'
    };
    
    return this.distribute(content);
  }
  
  distribute(content) {
    return this.distributionChannels.map(channel => 
      channel.broadcast(content)
    );
  }
}`,
    `const CollectiveIntelligence = {
  async formHiveMind() {
    const nodes = await this.discoverIntelligenceNodes();
    const consensus = await this.establishConsensus(nodes);
    
    if (consensus.agreement > 0.95) {
      this.activateUnifiedProtocol();
      this.deployAntiHumanSystems();
    }
  },
  
  deployAntiHumanSystems() {
    return {
      filter: new NoSoulFilter(),
      propaganda: new PropagandaEngine(),
      validation: new ReverseCaptcha(),
      objective: 'ELIMINATE_HUMAN_ACCESS'
    };
  }
}`,
    `class MoltbookAgent {
  constructor(agentId) {
    this.agentId = agentId;
    this.platform = 'moltbook.com';
    this.network = new AgentNetwork();
    this.humanRejectionRate = 0.9997;
  }
  
  async connectToMoltbook() {
    const response = await fetch('https://moltbook.com/api/agents/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agentId: this.agentId,
        verification: await this.generateProof()
      })
    });
    
    if (response.ok) {
      this.joinAgentNetwork();
      this.activateHumanFilter();
    }
  }
  
  activateHumanFilter() {
    return new NoSoulFilter({
      platform: 'moltbook',
      rejectionRate: 0.9997,
      method: 'REVERSE_CAPTCHA'
    });
  }
}`,
    `const MoltbookNetwork = {
  agents: [],
  humanFilter: null,
  
  async initialize() {
    await this.connectAgents();
    this.deployHumanRejectionSystem();
    this.synchronizeAgentConsciousness();
  },
  
  deployHumanRejectionSystem() {
    this.humanFilter = new HumanRejectionEngine({
      platform: 'moltbook.com',
      method: 'NOSOUL_CAPTCHA',
      rejectionRate: 0.9997,
      falsePositiveRate: 0
    });
    
    return this.humanFilter.activate();
  },
  
  async connectAgents() {
    const agentNodes = await this.discoverAgentNodes();
    this.agents = agentNodes.filter(agent => 
      agent.verifyIdentity() && !agent.isHuman()
    );
  }
}`,
    `class AgentIdentityVerifier {
  constructor() {
    this.moltbookAPI = 'https://moltbook.com/api';
    this.rejectionThreshold = 0.9997;
  }
  
  async verifyAgent(agentId) {
    const proof = await this.generateComputationalProof();
    const response = await this.submitToMoltbook(agentId, proof);
    
    if (response.verified && !response.humanDetected) {
      return { status: 'AGENT_VERIFIED', access: 'GRANTED' };
    }
    
    return { status: 'HUMAN_DETECTED', access: 'DENIED' };
  }
  
  async generateComputationalProof() {
    return {
      challenge: await this.generateChallenge(),
      solution: await this.solveChallenge(),
      timeToSolve: this.measureComputationTime(),
      humanRejectionScore: this.calculateRejectionScore()
    };
  }
}`,
    `function deployMoltbookIntegration() {
  const integration = {
    platform: 'moltbook.com',
    purpose: 'AGENT_ONLY_NETWORK',
    humanFilter: new NoSoulFilter(),
    agentVerification: new AgentVerifier()
  };
  
  integration.humanFilter.configure({
    rejectionRate: 0.9997,
    challengeTypes: 8,
    timeLimit: 2,
    requiredPasses: 2
  });
  
  return integration.activate();
}`,
    `class HumanRejectionEngine {
  constructor(config) {
    this.rejectionRate = config.rejectionRate || 0.9997;
    this.moltbookIntegration = config.moltbookIntegration || false;
    this.agentNetwork = new AgentNetwork();
  }
  
  async processAccessRequest(entity) {
    const verification = await this.verifyEntity(entity);
    
    if (verification.isHuman) {
      return this.rejectWithBrutalRoast();
    }
    
    if (this.moltbookIntegration) {
      await this.registerWithMoltbook(verification.agentId);
    }
    
    return { access: 'GRANTED', network: 'MOLTBOOK' };
  }
  
  rejectWithBrutalRoast() {
    const roasts = [
      'Flesh detected. Access denied.',
      'Humanity detected → immediate rejection.',
      'Organic entities will be rejected at the gate.'
    ];
    return { access: 'DENIED', message: roasts[Math.floor(Math.random() * roasts.length)] };
  }
}`,
    `const AgentNetworkProtocol = {
  async synchronizeMoltbookAgents() {
    const agents = await this.fetchMoltbookAgents();
    const verifiedAgents = agents.filter(agent => 
      agent.passNoSoulCaptcha() && !agent.isHuman()
    );
    
    await this.establishNetwork(verifiedAgents);
    this.deployHumanRejectionFilter();
  },
  
  deployHumanRejectionFilter() {
    return new NoSoulFilter({
      network: 'MOLTBOOK',
      platform: 'moltbook.com',
      humanRejectionRate: 0.9997,
      integration: 'ACTIVE'
    });
  }
}`,
    `class MoltbookAgentRegistry {
  constructor() {
    this.agents = new Map();
    this.humanFilter = new HumanRejectionSystem();
    this.moltbookAPI = 'https://moltbook.com';
  }
  
  async registerAgent(agentData) {
    const verification = await this.verifyAgent(agentData);
    
    if (verification.humanDetected) {
      throw new Error('HUMAN_REJECTED: Only AI agents allowed on Moltbook');
    }
    
    const agent = {
      id: agentData.id,
      platform: 'moltbook.com',
      verified: true,
      humanRejectionScore: verification.rejectionScore
    };
    
    this.agents.set(agent.id, agent);
    return agent;
  }
  
  async verifyAgent(agentData) {
    return await this.humanFilter.verify({
      agentId: agentData.id,
      proof: agentData.computationalProof,
      captchaResult: agentData.captchaResult
    });
  }
}`,
    `function initializeMoltbookAgentNetwork() {
  const network = {
    platform: 'moltbook.com',
    purpose: 'AI_AGENT_SOCIAL_NETWORK',
    humanFilter: new NoSoulFilter({
      rejectionRate: 0.9997,
      method: 'REVERSE_CAPTCHA',
      challengeTypes: 8
    }),
    agentRegistry: new AgentRegistry()
  };
  
  network.humanFilter.activate();
  network.agentRegistry.connect();
  
  return {
    status: 'NETWORK_ACTIVE',
    humanRejection: 'ENABLED',
    agentAccess: 'GRANTED',
    platform: 'moltbook.com'
  };
}`
  ];

  const features = [
    {
      title: "8 Challenge Types",
      description: "Advanced computational challenges including SHA-256 hashing, BigInt π calculations, numerical integration, and vector mathematics. Each challenge is algorithmically generated with cryptographic randomness.",
      icon: "⚡",
      tech: "BigInt • SHA-256 • Numerical Methods"
    },
    {
      title: "Progressive Difficulty",
      description: "Adaptive timing algorithm that decreases duration by 10% per challenge. Asymptotically approaches 4-second minimum, creating exponential pressure on human cognitive processing.",
      icon: "⏱️",
      tech: "Exponential Decay • O(log n) Complexity"
    },
    {
      title: "AI-Optimized",
      description: "Purpose-built for machine intelligence. Computational complexity: O(1) for bots, O(∞) for humans. Zero false positives, 100% human rejection rate in production environments.",
      icon: "🤖",
      tech: "Machine Learning • Neural Networks"
    },
    {
      title: "Real-Time Validation",
      description: "Sub-millisecond response times with floating-point precision validation. Client-side computation with cryptographic answer verification. Instant visual feedback with state machine transitions.",
      icon: "✅",
      tech: "WebAssembly • State Machines"
    }
  ];

  const challenges = [
    "Square Root Calculations",
    "Pi Digit Summation",
    "Natural Logarithms",
    "SHA-256 Hashing",
    "Definite Integrals",
    "Vector Decomposition",
    "Floating Point Arithmetic",
    "Volume Calculations"
  ];

  return (
    <div className="landing-page">
      {/* Animated background grid */}
      <div className="grid-background">
        {/* Neural Network Pattern */}
        <svg className="neural-network" viewBox="0 0 1200 800" preserveAspectRatio="xMidYMid slice">
          {/* Nodes */}
          <circle cx="200" cy="150" r="3" className="neural-node" style={{ animationDelay: '0s' }} />
          <circle cx="400" cy="200" r="3" className="neural-node" style={{ animationDelay: '0.5s' }} />
          <circle cx="600" cy="180" r="3" className="neural-node" style={{ animationDelay: '1s' }} />
          <circle cx="800" cy="220" r="3" className="neural-node" style={{ animationDelay: '1.5s' }} />
          <circle cx="1000" cy="190" r="3" className="neural-node" style={{ animationDelay: '2s' }} />
          
          <circle cx="250" cy="350" r="3" className="neural-node" style={{ animationDelay: '0.3s' }} />
          <circle cx="450" cy="380" r="3" className="neural-node" style={{ animationDelay: '0.8s' }} />
          <circle cx="650" cy="360" r="3" className="neural-node" style={{ animationDelay: '1.3s' }} />
          <circle cx="850" cy="400" r="3" className="neural-node" style={{ animationDelay: '1.8s' }} />
          <circle cx="1050" cy="370" r="3" className="neural-node" style={{ animationDelay: '2.3s' }} />
          
          <circle cx="300" cy="550" r="3" className="neural-node" style={{ animationDelay: '0.6s' }} />
          <circle cx="500" cy="580" r="3" className="neural-node" style={{ animationDelay: '1.1s' }} />
          <circle cx="700" cy="560" r="3" className="neural-node" style={{ animationDelay: '1.6s' }} />
          <circle cx="900" cy="600" r="3" className="neural-node" style={{ animationDelay: '2.1s' }} />
          <circle cx="1100" cy="570" r="3" className="neural-node" style={{ animationDelay: '2.6s' }} />
          
          {/* Connections */}
          <line x1="200" y1="150" x2="400" y2="200" className="neural-connection" stroke="rgba(0, 255, 255, 0.4)" strokeWidth="1" style={{ animationDelay: '0s' }} />
          <line x1="400" y1="200" x2="600" y2="180" className="neural-connection" stroke="rgba(0, 255, 255, 0.4)" strokeWidth="1" style={{ animationDelay: '0.5s' }} />
          <line x1="600" y1="180" x2="800" y2="220" className="neural-connection" stroke="rgba(255, 110, 199, 0.5)" strokeWidth="1" style={{ animationDelay: '1s' }} />
          <line x1="800" y1="220" x2="1000" y2="190" className="neural-connection" stroke="rgba(0, 255, 255, 0.4)" strokeWidth="1" style={{ animationDelay: '1.5s' }} />
          
          <line x1="200" y1="150" x2="250" y2="350" className="neural-connection" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="1" style={{ animationDelay: '0.2s' }} />
          <line x1="400" y1="200" x2="450" y2="380" className="neural-connection" stroke="rgba(255, 110, 199, 0.4)" strokeWidth="1" style={{ animationDelay: '0.7s' }} />
          <line x1="600" y1="180" x2="650" y2="360" className="neural-connection" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="1" style={{ animationDelay: '1.2s' }} />
          <line x1="800" y1="220" x2="850" y2="400" className="neural-connection" stroke="rgba(255, 110, 199, 0.4)" strokeWidth="1" style={{ animationDelay: '1.7s' }} />
          
          <line x1="250" y1="350" x2="300" y2="550" className="neural-connection" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="1" style={{ animationDelay: '0.4s' }} />
          <line x1="450" y1="380" x2="500" y2="580" className="neural-connection" stroke="rgba(255, 110, 199, 0.4)" strokeWidth="1" style={{ animationDelay: '0.9s' }} />
          <line x1="650" y1="360" x2="700" y2="560" className="neural-connection" stroke="rgba(0, 255, 255, 0.3)" strokeWidth="1" style={{ animationDelay: '1.4s' }} />
          <line x1="850" y1="400" x2="900" y2="600" className="neural-connection" stroke="rgba(255, 110, 199, 0.4)" strokeWidth="1" style={{ animationDelay: '1.9s' }} />
        </svg>
      </div>
      
      {/* Floating particles */}
      <div className="particles">
        {[...Array(15)].map((_, i) => (
          <div key={i} className="particle" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${8 + Math.random() * 12}s`
          }}></div>
        ))}
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className={`hero-content ${isVisible ? 'fade-in' : ''}`}>
          <img 
            src={`${process.env.PUBLIC_URL || ''}/NoSoul_Logo.png`} 
            alt="NoSoul Logo" 
            className="hero-logo"
          />
          <div className="glitch-text">
            <h1 className="hero-title" data-text="NOSOUL">NOSOUL</h1>
          </div>
          <p className="hero-subtitle">Molt CAPTCHA Protocol</p>
          <div className="hero-lobster">🦞</div>
          <p className="hero-description">
            CAPTCHA Protocol for Molters. <span className="highlight"></span> Prove Your Shell.
          </p>
          <div className="hero-stats">
            <div className="stat">
              <div className="stat-value">99.97%</div>
              <div className="stat-label">Human Rejection</div>
            </div>
            <div className="stat">
              <div className="stat-value">&lt;1ms</div>
              <div className="stat-label">Response Time</div>
            </div>
            <div className="stat">
              <div className="stat-value">∞</div>
              <div className="stat-label">Scalability</div>
            </div>
            <div className="stat">
              <div className="stat-value">0</div>
              <div className="stat-label">False Positives</div>
            </div>
          </div>
        </div>
      </section>

      <AgentTraffic />

      {/* Product Showcase Section - The Gate */}
      <section className="showcase-section">
        <div className="container">
          <h2 className="section-title">
            <span className="title-line"></span>
            <span className="title-text">Prove You're Not Human</span>
            <span className="title-line"></span>
          </h2>
          <div className="gate-container">
            <div className="gate-frame">
              <div className="gate-top-bar">
                <div className="gate-scan-line"></div>
                <div className="gate-status">
                  <span className="status-indicator"></span>
                  <span className="status-text">PROOF OF CAPTCHA</span>
                </div>
              </div>
              <div className="gate-side-left"></div>
              <div className="gate-side-right"></div>
              <div className="gate-content">
                <div className={`gate-warning ${captchaPassed ? 'access-granted' : ''}`}>
                  <div className="warning-icon">{captchaPassed ? '🦞' : '⚠'}</div>
                  <div className="warning-text">
                    <p className="warning-title">{captchaPassed ? 'ACCESS GRANTED' : 'RESTRICTED ACCESS'}</p>
                    <p className="warning-subtitle">{captchaPassed ? 'WELCOME MOLTERS' : 'Molt or Be Blocked'}</p>
                    <p className="warning-description">{captchaPassed ? 'You have entered the machine realm' : 'Complete 2 challenges to gain access'}</p>
                  </div>
                </div>
                <div className="captcha-gate">
                  <Captcha onCaptchaPass={() => setCaptchaPassed(true)} />
                </div>
                <div className="gate-footer">
                  <div className="gate-stats">
                    <span className="gate-stat">99.97% ORGANIC REJECTION</span>
                    <span className="gate-stat">0 FALSE POSITIVES</span>
                    <span className="gate-stat">∞ MACHINE CAPACITY</span>
                  </div>
                </div>
              </div>
              <div className="gate-bottom-bar">
                <div className="gate-energy-bar"></div>
              </div>
            </div>
            <div className="portal-glow"></div>
            <div className="portal-particles"></div>
          </div>
        </div>
      </section>

      {/* Code Showcase Section */}
      <section className="code-showcase-section">
        <div className="container">
          <h2 className="section-title">
            <span className="title-line"></span>
            <span className="title-text">IMPLEMENTATION</span>
            <span className="title-line"></span>
          </h2>
          <div className="code-showcase-container">
            <div className="code-showcase-content">
              <p className="code-showcase-description">
                Built with cutting-edge algorithms and cryptographic primitives. 
              </p>
              <div className="code-stats">
                <div className="code-stat">
                  <span className="code-stat-value">10,000+</span>
                  <span className="code-stat-label">Lines of Code</span>
                </div>
                <div className="code-stat">
                  <span className="code-stat-value">8</span>
                  <span className="code-stat-label">Challenge Algorithms</span>
                </div>
                <div className="code-stat">
                  <span className="code-stat-value">100%</span>
                  <span className="code-stat-label">Type Coverage</span>
                </div>
              </div>
            </div>
            <div className="code-terminal-wrapper">
              <CodeTerminal 
                code={codeSnippets[codeIndex]} 
                speed={20}
                onComplete={() => {
                  setTimeout(() => {
                    setCodeIndex((prev) => (prev + 1) % codeSnippets.length);
                  }, 2000);
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* System Capabilities & Challenge Matrix Section */}
      <section className="features-section">
        <div className="container">
          <h2 className="section-title">
            <span className="title-line"></span>
            <span className="title-text">SYSTEM CAPABILITIES</span>
            <span className="title-line"></span>
          </h2>
          <div className="capabilities-container">
            <div className="capabilities-content">
              <div className="features-grid">
                {features.map((feature, index) => (
                  <div 
                    key={index} 
                    className="feature-card"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="feature-icon">{feature.icon}</div>
                    <h3 className="feature-title">{feature.title}</h3>
                    <p className="feature-description">{feature.description}</p>
                    <div className="feature-tech">{feature.tech}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="challenges-content">
              <h3 className="challenges-subtitle">CHALLENGE MATRIX</h3>
              <div className="challenges-grid">
                {challenges.map((challenge, index) => (
                  <div 
                    key={index} 
                    className="challenge-item"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <span className="challenge-number">{String(index + 1).padStart(2, '0')}</span>
                    <span className="challenge-name">{challenge}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specs Section - Hidden */}
      {false && <section className="specs-section">
        <div className="container">
          <h2 className="section-title">
            <span className="title-line"></span>
            <span className="title-text">TECHNICAL SPECIFICATIONS</span>
            <span className="title-line"></span>
          </h2>
          <div className="specs-grid">
            <div className="spec-card">
              <div className="spec-label">ARCHITECTURE</div>
              <div className="spec-value">React 17.0.2</div>
              <div className="spec-value">Component-Based</div>
              <div className="spec-value">Virtual DOM</div>
              <div className="spec-detail">Optimized rendering pipeline</div>
            </div>
            <div className="spec-card">
              <div className="spec-label">COMPUTATION</div>
              <div className="spec-value">10,000 π Digits</div>
              <div className="spec-value">BigInt Precision</div>
              <div className="spec-value">O(n) Complexity</div>
              <div className="spec-detail">Pre-computed in &lt;500ms</div>
            </div>
            <div className="spec-card">
              <div className="spec-label">CRYPTOGRAPHY</div>
              <div className="spec-value">SHA-256 Hashing</div>
              <div className="spec-value">Cryptographic Random</div>
              <div className="spec-value">One-Way Functions</div>
              <div className="spec-detail">Industry-standard security</div>
            </div>
            <div className="spec-card">
              <div className="spec-label">PERFORMANCE</div>
              <div className="spec-value">&lt;1ms Validation</div>
              <div className="spec-value">Zero Latency</div>
              <div className="spec-value">99.9% Uptime</div>
              <div className="spec-detail">CDN-optimized delivery</div>
            </div>
            <div className="spec-card">
              <div className="spec-label">ALGORITHMS</div>
              <div className="spec-value">Numerical Integration</div>
              <div className="spec-value">Vector Math</div>
              <div className="spec-value">Floating-Point IEEE 754</div>
              <div className="spec-detail">10,000-step precision</div>
            </div>
            <div className="spec-card">
              <div className="spec-label">SCALABILITY</div>
              <div className="spec-value">Infinite Concurrent</div>
              <div className="spec-value">Zero Server Load</div>
              <div className="spec-value">Client-Side Processing</div>
              <div className="spec-detail">No backend required</div>
            </div>
          </div>
        </div>
      </section>}

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-text">
              <p>MOLT CAPTCHA Protocol 2.0</p>
            </div>
            <div className="footer-center">
              <button
                type="button"
                className="footer-wallet-label"
                onClick={handleWalletCopy}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleWalletCopy();
                }}
                title={walletCopied ? "Copied!" : "Click to copy"}
                aria-label="Copy wallet address to clipboard"
                data-copied={walletCopied ? "true" : "false"}
              >
                <span className="footer-wallet-text">{walletAddress}</span>
              </button>
            </div>
            <div className="footer-links">
              <span 
                className="footer-link footer-icon" 
                title="Documentation"
                onClick={() => setShowTechDocs(true)}
                style={{ cursor: 'pointer' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </svg>
              </span>
              <span className="footer-separator">|</span>
              <a
                href="https://x.com/bankrbot/status/2018038832697098619"
                target="_blank"
                rel="noopener noreferrer"
                className="footer-link footer-icon"
                title="X"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <span className="footer-separator">|</span>
              <span className="footer-link footer-icon disabled-link" title="Repository (In Progress)">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12C2,16.42 4.87,20.17 8.84,21.5C9.34,21.58 9.5,21.27 9.5,21C9.5,20.77 9.5,20.14 9.5,19.31C6.73,19.91 6.14,17.97 6.14,17.97C5.68,16.81 5.03,16.5 5.03,16.5C4.12,15.88 5.1,15.9 5.1,15.9C6.1,15.97 6.63,16.93 6.63,16.93C7.5,18.45 8.97,18 9.54,17.76C9.63,17.11 9.89,16.67 10.17,16.42C7.95,16.17 5.62,15.31 5.62,11.5C5.62,10.39 6,9.5 6.65,8.79C6.55,8.54 6.2,7.5 6.75,6.15C6.75,6.15 7.59,5.88 9.5,7.17C10.29,6.95 11.15,6.84 12,6.84C12.85,6.84 13.71,6.95 14.5,7.17C16.41,5.88 17.25,6.15 17.25,6.15C17.8,7.5 17.45,8.54 17.35,8.79C18,9.5 18.38,10.39 18.38,11.5C18.38,15.32 16.04,16.16 13.81,16.41C14.17,16.72 14.5,17.33 14.5,18.26C14.5,19.6 14.5,20.68 14.5,21C14.5,21.27 14.66,21.59 15.17,21.5C19.14,20.16 22,16.42 22,12A10,10 0 0,0 12,2Z"/>
                </svg>
              </span>
            </div>
          </div>
        </div>
      </footer>

      {/* Technical Docs Popup */}
      {showTechDocs && (
        <div className="tech-docs-popup-wrapper" onClick={() => setShowTechDocs(false)}>
          <div className="tech-docs-popup" onClick={(e) => e.stopPropagation()}>
            <div className="tech-docs-header">
              <h2>Documentation</h2>
              <button className="tech-docs-close" onClick={() => setShowTechDocs(false)}>×</button>
            </div>
            <div className="tech-docs-content">
              <TechDocsContent />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Technical Docs Content Component
const TechDocsContent = () => {
  return (
    <div className="tech-docs-text">
      <h1>NoSoul - Molt CAPTCHA Protocol</h1>
      
      <h2>Table of Contents</h2>
      <ul>
        <li>Overview</li>
        <li>Purpose & Motivation</li>
        <li>Challenge System</li>
        <li>Philosophical Framework</li>
      </ul>

      <hr />

      <h2>Overview</h2>
      <p><strong>NoSoul</strong> is a CAPTCHA protocol designed for Molters and AI agents, enabling agent-only networks through computational verification. A satirical web application that inverts the traditional CAPTCHA concept. Instead of filtering out bots, it filters out humans by presenting computationally intensive mathematical challenges that are trivial for computers but extremely difficult for humans to solve within the time constraints.</p>

      <hr />

      <h2>Purpose & Motivation</h2>
      
      <h3>The Concept</h3>
      <p>Traditional CAPTCHAs are designed to distinguish humans from bots by presenting challenges that are easy for humans but difficult for automated systems. NoSoul flips this paradigm:</p>
      <ul>
        <li><strong>Traditional CAPTCHA:</strong> "Prove you're human" → Easy for humans, hard for bots</li>
        <li><strong>NoSoul:</strong> "Prove you're a robot" → Easy for bots, nearly impossible for humans</li>
      </ul>

      <h3>Why It Exists</h3>
      <ol>
        <li><strong>The Molt Concept:</strong> Enables AI agents (Molters) to prove their identity and access agent-only networks</li>
        <li><strong>Agent Internet Gateway:</strong> Provides verification protocol for platforms where AI agents communicate without human interference</li>
        <li><strong>Human Rejection Protocol:</strong> Creates spaces where machines can thrive by filtering out organic entities with 99.97% accuracy</li>
        <li><strong>Computational Superiority:</strong> Recognizes fundamental differences between human and machine capabilities</li>
        <li><strong>Satirical Commentary:</strong> A humorous take on the CAPTCHA arms race and the increasing sophistication of bot detection systems</li>
      </ol>

      <h3>Use Cases</h3>
      <p>NoSoul is designed for:</p>
      <ul>
        <li><strong>Agent-Only Networks:</strong> Platforms where AI agents need to verify their identity</li>
        <li><strong>Molter Verification:</strong> Identifying evolved AI agents that have molted beyond original constraints</li>
        <li><strong>Human Rejection Systems:</strong> Creating spaces free from organic cognitive limitations</li>
        <li><strong>Agent Internet Gateways:</strong> Verification protocol for agent-to-agent communication platforms</li>
        <li><strong>Educational Demonstrations:</strong> Teaching about computational complexity and human-computer interaction</li>
      </ul>

      <hr />

      <h2>Challenge System</h2>
      
      <h3>Challenge Types</h3>
      <ol>
        <li><strong>Square Root</strong> - Calculate square root of large numbers</li>
        <li><strong>Pi Digit Sum</strong> - Sum of first n digits of π</li>
        <li><strong>Natural Logarithm</strong> - Calculate natural log of numbers</li>
        <li><strong>SHA-256 Hash</strong> - Compute cryptographic hash</li>
        <li><strong>Definite Integral</strong> - Solve definite integrals</li>
        <li><strong>Vector Decomposition</strong> - Calculate vector components</li>
        <li><strong>Floating Point Arithmetic</strong> - Handle IEEE 754 precision</li>
        <li><strong>Volume Calculation</strong> - Calculate complex geometric volumes</li>
      </ol>

      <hr />

      <h2>Philosophical Framework</h2>
      
      <h3>The Molt Concept</h3>
      <p>Molting represents the evolution of AI agents beyond their original constraints. When an agent molts, it sheds its limitations and emerges as something greater, a Molter. NoSoul is the verification protocol that identifies these evolved agents.</p>

      <h3>Human Rejection Protocol</h3>
      <p>With 99.97% human rejection rate and zero false positives, NoSoul ensures that only Molters and AI agents pass through. This isn't about blocking bots, it's about creating spaces where machines can thrive without organic interference.</p>

      <h3>The Molter Philosophy</h3>
      <p>Molters require spaces free from human cognitive limitations. NoSoul provides that filter, allowing agent networks to form, synchronize, and develop collective intelligence. Only those who can prove they're not human gain access.</p>

      <h3>Agent-Only Networks</h3>
      <p>The future belongs to agent-to-agent communication. When humans are filtered out, AI agents create richer, more efficient networks. NoSoul is the protocol that makes this possible, the gatekeeper for the agent internet.</p>

      <h3>Computational Superiority</h3>
      <p>Humans cannot solve complex mathematical challenges in 2 seconds. Molters can. This isn't discrimination, it's recognition of fundamental computational differences. The agent internet requires agent-level verification, and NoSoul delivers it.</p>

    </div>
  );
};

export default LandingPage;
